import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Search, Mail, Phone, MapPin, Sparkles, Plus, X } from "lucide-react";
import { useSearchCandidatesByAI } from "@/hooks/lists/useListDetail";

interface AISearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAddCandidates: (skills: string[]) => Promise<void>;
  listId: string;
  isLoading: boolean;
}

export const AISearchModal = ({
  isOpen,
  onClose,
  onAddCandidates,
  listId,
  isLoading
}: AISearchModalProps) => {
  const { t } = useTranslation();
  const [skills, setSkills] = useState<string[]>([""]);
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [selectedCandidates, setSelectedCandidates] = useState<string[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  const searchCandidatesMutation = useSearchCandidatesByAI();

  const handleAddSkillField = () => {
    setSkills([...skills, ""]);
  };

  const handleRemoveSkillField = (index: number) => {
    if (skills.length > 1) {
      setSkills(skills.filter((_, i) => i !== index));
    }
  };

  const handleSkillChange = (index: number, value: string) => {
    const newSkills = [...skills];
    newSkills[index] = value;
    setSkills(newSkills);
  };

  const handleSearch = async () => {
    const validSkills = skills.filter(skill => skill.trim());
    if (validSkills.length === 0) return;

    setIsSearching(true);
    try {
      const results = await searchCandidatesMutation.mutateAsync({
        list_id: listId,
        skills: validSkills
      });
      setSearchResults(results);
    } catch (error) {
      console.error('Error searching candidates:', error);
    } finally {
      setIsSearching(false);
    }
  };

  const handleCandidateToggle = (candidateId: string) => {
    setSelectedCandidates(prev =>
      prev.includes(candidateId)
        ? prev.filter(id => id !== candidateId)
        : [...prev, candidateId]
    );
  };

  const handleSelectAll = () => {
    if (selectedCandidates.length === searchResults.length) {
      setSelectedCandidates([]);
    } else {
      setSelectedCandidates(searchResults.map(c => c.id));
    }
  };

  const handleAddCandidates = async () => {
    if (selectedCandidates.length === 0) return;
    
    const validSkills = skills.filter(skill => skill.trim());
    await onAddCandidates(validSkills);
    setSelectedCandidates([]);
    setSearchResults([]);
    setSkills([""]);
  };

  const handleClose = () => {
    setSelectedCandidates([]);
    setSearchResults([]);
    setSkills([""]);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>{t("lists.aiSearch.title", "AI-Powered Candidate Search")}</DialogTitle>
        </DialogHeader>

        <div className="flex-1 flex flex-col space-y-4">
          {/* Skills Input */}
          <div className="space-y-3">
            <label className="text-sm font-medium">
              {t("lists.aiSearch.skills", "Skills & Requirements")}
            </label>
            {skills.map((skill, index) => (
              <div key={index} className="flex space-x-2">
                <Input
                  placeholder={t("lists.aiSearch.skillPlaceholder", "Enter a skill or requirement...")}
                  value={skill}
                  onChange={(e) => handleSkillChange(index, e.target.value)}
                />
                {skills.length > 1 && (
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={() => handleRemoveSkillField(index)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
            ))}
            <Button
              type="button"
              variant="outline"
              onClick={handleAddSkillField}
              className="w-fit"
            >
              <Plus className="h-4 w-4 mr-2" />
              {t("lists.aiSearch.addSkill", "Add Skill")}
            </Button>
          </div>

          {/* Search Button */}
          <Button
            onClick={handleSearch}
            disabled={skills.every(skill => !skill.trim()) || isSearching}
            className="w-fit"
          >
            <Sparkles className="h-4 w-4 mr-2" />
            {isSearching 
              ? t("lists.aiSearch.searching", "Searching...")
              : t("lists.aiSearch.search", "Search Candidates")
            }
          </Button>

          {/* Results */}
          {searchResults.length > 0 && (
            <>
              {/* Select All */}
              <div className="flex items-center space-x-2">
                <Checkbox
                  checked={selectedCandidates.length === searchResults.length && searchResults.length > 0}
                  onCheckedChange={handleSelectAll}
                />
                <span className="text-sm text-gray-600">
                  {t("lists.aiSearch.selectAll", "Select All")} ({searchResults.length})
                </span>
              </div>

              {/* Results List */}
              <div className="flex-1 overflow-y-auto border rounded-sm">
                <div className="divide-y">
                  {searchResults.map((candidate) => (
                    <div
                      key={candidate.id}
                      className="flex items-center space-x-3 p-4 hover:bg-gray-50"
                    >
                      <Checkbox
                        checked={selectedCandidates.includes(candidate.id)}
                        onCheckedChange={() => handleCandidateToggle(candidate.id)}
                      />
                      <Avatar className="h-10 w-10">
                        <AvatarFallback>
                          {candidate.name?.split(' ').map(n => n[0]).join('').toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <h4 className="text-sm font-medium text-gray-900 truncate">
                            {candidate.name}
                          </h4>
                          {candidate.function && (
                            <Badge variant="secondary" className="ml-2">
                              {candidate.function}
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center space-x-4 mt-1 text-sm text-gray-500">
                          {candidate.email && (
                            <div className="flex items-center">
                              <Mail className="h-3 w-3 mr-1" />
                              <span className="truncate">{candidate.email}</span>
                            </div>
                          )}
                          {candidate.phone && (
                            <div className="flex items-center">
                              <Phone className="h-3 w-3 mr-1" />
                              <span>{candidate.phone}</span>
                            </div>
                          )}
                          {candidate.location && (
                            <div className="flex items-center">
                              <MapPin className="h-3 w-3 mr-1" />
                              <span className="truncate">{candidate.location}</span>
                            </div>
                          )}
                        </div>
                        {candidate.skills && candidate.skills.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {candidate.skills.slice(0, 3).map((skill, index) => (
                              <Badge key={index} variant="outline" className="text-xs">
                                {skill}
                              </Badge>
                            ))}
                            {candidate.skills.length > 3 && (
                              <Badge variant="outline" className="text-xs">
                                +{candidate.skills.length - 3}
                              </Badge>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          {searchCandidatesMutation.isError && (
            <div className="text-red-600 text-sm">
              {t("lists.aiSearch.error", "Failed to search candidates. Please try again.")}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between pt-4 border-t">
          <div className="text-sm text-gray-600">
            {selectedCandidates.length > 0 && (
              <span>
                {t("lists.aiSearch.selected", "{{count}} selected", { count: selectedCandidates.length })}
              </span>
            )}
          </div>
          <div className="flex space-x-2">
            <Button variant="outline" onClick={handleClose}>
              {t("common.cancel", "Cancel")}
            </Button>
            <Button
              onClick={handleAddCandidates}
              disabled={selectedCandidates.length === 0 || isLoading}
            >
              {isLoading 
                ? t("lists.aiSearch.adding", "Adding...")
                : t("lists.aiSearch.add", "Add Candidates")
              }
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
